import React, { useEffect, useMemo, useRef } from 'react';

interface Star { cx: number; cy: number; r: number; o: number; }

function mulberry32(seed: number) {
  return () => {
    let t = (seed += 0x6d2b79f5) >>> 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeStars(seed: number, count: number, rMin: number, rMax: number, oMin: number, oMax: number): Star[] {
  const r = mulberry32(seed);
  const list: Star[] = [];
  for (let i = 0; i < count; i++) {
    list.push({
      cx: r() * 100,
      cy: r() * 100,
      r: rMin + r() * (rMax - rMin),
      o: oMin + r() * (oMax - oMin),
    });
  }
  return list;
}

/**
 * 4 layers of parallax stars. Stars are TINY pinpoints (true to scale),
 * varying counts and sizes per layer. Mouse-tilt parallax driven by a single
 * rAF; transforms applied directly via style for compositor-thread paint.
 *
 * Mobile: parallax disabled (no mouse hover) to save cycles.
 */
const LAYERS = [
  { seed: 1, count: 14, rMin: 0.10, rMax: 0.22, oMin: 0.30, oMax: 0.55, mult: 0.05 },
  { seed: 2, count: 20, rMin: 0.10, rMax: 0.20, oMin: 0.40, oMax: 0.70, mult: 0.10 },
  { seed: 3, count: 26, rMin: 0.08, rMax: 0.18, oMin: 0.55, oMax: 0.85, mult: 0.18 },
  { seed: 4, count: 30, rMin: 0.08, rMax: 0.16, oMin: 0.65, oMax: 0.95, mult: 0.30 },
];

export const ParallaxStarField: React.FC<{ animate: boolean }> = ({ animate }) => {
  const layersRef = useRef<HTMLDivElement>(null);
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const isMobile = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
    []
  );

  const layers = useMemo(
    () => LAYERS.map((L) => ({ ...L, stars: makeStars(L.seed, L.count, L.rMin, L.rMax, L.oMin, L.oMax) })),
    []
  );

  useEffect(() => {
    if (!animate || !layersRef.current || isMobile) return; // skip rAF + mousemove on touch devices
    const root = layersRef.current;
    const layerEls = Array.from(root.querySelectorAll<SVGSVGElement>('[data-parallax]'));

    const onMove = (e: MouseEvent) => {
      target.current.x = (e.clientX / window.innerWidth - 0.5) * 100;
      target.current.y = (e.clientY / window.innerHeight - 0.5) * 100;
    };

    let lastT = 0;
    const tick = (t: number) => {
      // throttle to ~30fps for parallax — humans can't tell the difference for slow drift
      if (t - lastT > 33) {
        current.current.x += (target.current.x - current.current.x) * 0.05;
        current.current.y += (target.current.y - current.current.y) * 0.05;
        for (const el of layerEls) {
          const m = parseFloat(el.dataset.parallax || '0');
          const tx = current.current.x * m * 0.5;
          const ty = current.current.y * m * 0.5;
          el.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0)`;
        }
        lastT = t;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [animate, isMobile]);

  return (
    <div ref={layersRef} aria-hidden>
      {layers.map((L, idx) => (
        <svg
          key={idx}
          className={`hero__stars hero__stars--l${idx + 1}`}
          data-parallax={L.mult}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ shapeRendering: 'optimizeSpeed' }}
        >
          {L.stars.map((s, i) => (
            <circle key={i} cx={s.cx} cy={s.cy} r={s.r} fill="#ffffff" opacity={s.o} />
          ))}
        </svg>
      ))}
    </div>
  );
};
